import { StemType } from '@arpeggi/shared'
import { applySnapshot, clone, detach, flow, getSnapshot, Instance, types } from 'mobx-state-tree'
import { ForDirectExtend } from '../../../../features/mobx'
import { ClipStore, IClipStore } from '../clip/ClipStore'
import { ISampleWithEffectsStore, SampleWithEffectsStore } from '../sample/SampleWithEffectsStore'
import { StemSettingsStore } from '../StemSettingsStore'
import { StemStore } from '../StemStore'

export const AudioStemStore = StemStore.named('audio')
  .props({
    stemType: StemType.AUDIO,
    clips: types.array(ClipStore)
  })
  .views((self) => ({
    getClipsWithIndex(index): IClipStore {
      return self.clips[index]
    },
    getClipByID(ID: string): IClipStore | null {
      const clip = self.clips.find((clip) => clip.uniqueId === ID)
      return clip ? clip : null
    },
    getCurrentSample() {
      return self.clips[0]?.sampleWithEffects
    },
    type(): string {
      return self.stemType
    },
    // gets the tick of the end of the last played clip in the composition for this stem
    get stemEndingInTicks(): number {
      let lastTickEnd = 0
      self.clips.forEach((clip) => (lastTickEnd = Math.max(lastTickEnd, clip.endInComposition48n)))
      return lastTickEnd
    }
  }))
  .actions((self) => {
    function init(
      songDisplayLength: number,
      sampleWithEffects?: ISampleWithEffectsStore,
      tempo?: number
    ) {
      if (sampleWithEffects) {
        const clip = ClipStore.create()
        const newSampleWithEffects = SampleWithEffectsStore.create()
        newSampleWithEffects.cloneFrom(sampleWithEffects)
        if (!tempo) {
          throw 'Tempo must be defined'
        }
        clip.init(newSampleWithEffects, tempo)
        self.clips.push(clip)
      }
      self.stemSettings = StemSettingsStore.create()
      self.stemSettings.initialize(songDisplayLength)
      self.stemSettings.setStemGain(0.5)
    }

    /**
     * Cuts a clip and returns the second clip that is returned from the cutting. Automatically
     * adds the new clip to the stem. The current uniqueID is maintained for the current clip (but not the new one).
     * Must be within the current clip's offset and endInComposition values.
     * @param clipID the uniqueID of the clip to cut
     * @param tick the tick (0 < tick < clip.getAudioSourceDuration48n()) to cut the clip at
     * @param tempo the tempo of the current song
     * @return the second clip that is produced by cutting this clip
     */
    const cutClipAtTick = (clipID: string, tick: number, tempo: number): IClipStore | null => {
      const clip = self.getClipByID(clipID)
      if (!clip) return null

      tick = Math.floor(tick)
      const currentStart = clip.startInComposition48n
      const currentOffset = clip.offset48n
      const currentEnd = clip.endInComposition48n
      if (
        tick <= 0 ||
        tick <= currentOffset ||
        tick >= currentEnd - (currentStart - currentOffset) ||
        tick >= clip.getAudioSourceDuration48n(tempo)
      ) {
        return null
      }

      const newClip = clone(clip)
      newClip.generateClipID()

      const tickToComposition48n = tick + (currentStart - currentOffset)
      clip.updateEndInComposition(tickToComposition48n)
      newClip.setStartInteral(tickToComposition48n)
      newClip.setOffsetInteral(tick)
      newClip.setEndInteral(currentEnd)

      addClip(newClip)
      return newClip
    }

    /** Adds a new clip from a sample to the stem, section is the bar the
     * clip should be added to
     */
    const addClipFromSample = flow(function* (
      sampleIndex: number,
      tempo: number,
      start48n: number = 0
    ) {
      const sampleWithEffects = SampleWithEffectsStore.create()
      sampleWithEffects.updateSample(sampleIndex)
      const clip = ClipStore.create()
      yield clip.init(sampleWithEffects, tempo, start48n)
      addClip(clip)
    })

    /** Adds a new clip from a remixed stem to the stem, section is the bar the
     * clip should be added to
     */
    const addClipFromStem = flow(function* (
      remixStemIndex: number,
      tempo: number,
      start48n: number = 0
    ) {
      const sampleWithEffects = SampleWithEffectsStore.create()
      sampleWithEffects.updateSample(remixStemIndex)
      sampleWithEffects.updateMediaType('stem')
      const clip = ClipStore.create()
      yield clip.init(sampleWithEffects, tempo, start48n)
      addClip(clip)
    })

    /** Adds a clip to the stem */
    function addClip(clip: IClipStore) {
      self.clips.push(clip)
    }

    /** Removes a clip from the stem
     * @return the removed clip */
    function removeClipByID(clipID: string): IClipStore {
      const removedClip = self.getClipByID(clipID)
      if (!removedClip) return null
      detach(removedClip)
      const newClips: IClipStore[] = self.clips.filter((clip) => clip.uniqueId !== clipID)
      self.clips = newClips as any
      return removedClip
    }

    /** Duplicates the selected clip, placing it right after it */
    function duplicateClipByID(clipID: string) {
      const clip = self.getClipByID(clipID)
      if (!clip) return

      const newClip = clone(clip)
      applySnapshot(newClip, getSnapshot(clip))
      newClip.generateClipID()
      const clipLength = newClip.endInComposition48n - newClip.startInComposition48n
      const newClipStart = newClip.endInComposition48n + 1
      newClip.updateStartInComposition(newClipStart)
      newClip.updateEndInComposition(newClipStart + clipLength)
      self.clips.push(newClip)
    }

    return {
      init,
      addClipFromSample,
      addClip,
      removeClipByID,
      duplicateClipByID,
      cutClipAtTick,
      addClipFromStem
    }
  })

export interface IAudioStemStore extends ForDirectExtend<Instance<typeof AudioStemStore>> {}
